(ns app.front.view.create-application.gis-search
  (:require [app.domain.property.model :as property-model]
            [app.front.ui :as ui]
            [app.front.util :as futil]
            [app.i18n.model :as i18n-model]
            [core.front.carbon :as cc]
            [core.front.event :refer [<sub >evt]]
            [core.front.feature-flags :as ff]
            [core.front.icons :as i]
            [potpuri.core :as p]
            [re-frame.core-instrumented :as rf]))

(def max-results 100)

(rf/reg-event-db ::reset-view
  (fn [db [_ initial-search]]
    (-> db
        (assoc ::address-search-results {:search-value (or initial-search "")}
               ::location-search-results {:results []}))))

(defn search-success-fn [k]
  (fn [db [_ _ body]]
    (let [input-value (get-in db [k :search-value])]
      (update db
              k
              assoc
              :pending? false
              :results (if (not-empty input-value)
                         body
                         [])))))

(rf/reg-event-db ::address-search-success
  (search-success-fn ::address-search-results))

(rf/reg-event-db ::location-search-success
  (search-success-fn ::location-search-results))

(rf/reg-event-fx ::address-search-error
  (fn [{:keys [db]} [_ on-error-evt _ {:keys [error]}]]
    {:db         (p/deep-merge db {::address-search-results {:pending? false
                                                             :results  []}})
     :dispatch-n (concat [[:notifications/add :toast :warning :error/connection-error-title error]]
                         (when on-error-evt
                           [on-error-evt]))}))

(defn- layer-ids [db layer-id-fn]
  (if (and (ifn? layer-id-fn)
           (ff/enabled? :feature-flag.id/public-works-location-enhancements))
    (or (layer-id-fn db) [])
    []))

(rf/reg-event-fx ::address-search-command
  (fn [{:keys [db]} [_ domain-group-id municipalities value layer-id-fn & [on-error-evt]]]
    {:comm/command {:command        :address/search
                    :data           {:domain-group-id domain-group-id
                                     :municipalities  municipalities
                                     :term            value
                                     :layer-ids       (layer-ids db layer-id-fn)
                                     :max-results     max-results}
                    :on-success     [::address-search-success]
                    :on-bad-gateway [::address-search-error on-error-evt]}
     :db           (assoc-in db [::address-search-results :pending?] true)}))

(rf/reg-event-fx ::location-search-command
  (fn [{:keys [db]} [_ municipalities value]]
    {:comm/command {:command    :address/search-existing-locations
                    :data       {:search-term          value
                                 :municipality/numbers municipalities}
                    :on-success [::location-search-success]}
     :db           (update db ::location-search-results assoc :pending? true)}))

(defn set-search-value [db k search-value]
  (if search-value
    (-> db
        (update k assoc :results [])
        (assoc-in [k :search-value] search-value))
    db))

(defn set-search-values [db search-value]
  (-> db
      (set-search-value ::address-search-results search-value)
      (set-search-value ::location-search-results search-value)))

(rf/reg-event-fx ::address-search-input
  (fn [{:keys [db]} [_ domain-group-id municipalities value & [on-error-evt skip-location-search? layer-id-fn]]]
    (if (and (string? value) (>= (count value) 3))
      {:dispatch-debounce (concat [{:id      ::do-address-search
                                    :timeout 500
                                    :action  :dispatch
                                    :event   [::address-search-command domain-group-id municipalities value layer-id-fn on-error-evt]}]
                                  (when-not skip-location-search?
                                    [{:id      ::do-location-search
                                      :timeout 500
                                      :action  :dispatch
                                      :event   [::location-search-command municipalities value]}]))
       :db                (set-search-values db value)}
      {:db (-> db
               (update ::address-search-results assoc :pending? false :results [] :search-value value)
               (update ::location-search-results assoc :pending? false :results [] :search-value value))})))

(rf/reg-event-fx ::address-search-initial
  (fn [{:keys [db]} [_ domain-group-id municipality value]]
    {:dispatch-n [[::address-search-command domain-group-id [municipality] value]
                  [::location-search-command [municipality] value]]
     :db         (set-search-values db value)}))

(rf/reg-event-db ::set-search-value
  (fn [db [_ search-value]]
    (set-search-values db search-value)))

(rf/reg-event-db ::clear-address-search
  (fn [db]
    (set-search-values db "")))

(rf/reg-sub ::address-search-results
  (fn [db _]
    (-> db ::address-search-results)))


(rf/reg-sub ::location-search-results
  (fn [db _]
    (-> db ::location-search-results)))

(rf/reg-sub ::pending?
  :<- [::address-search-results]
  (fn [{:keys [pending?]} _] pending?))

(defn- result-list-item
  [opts & children]
  (into [(if (:on-click opts)
           :li.p-2.hover:bg-gray-300.cursor-pointer
           :li.p-2.hover:bg-gray-300.cursor-default)
         (assoc opts :role "option" :tab-index 0)]
        children))

(defn- address-result-id [{:keys [property address]}]
  (str "address-"
       (or (:property/id property) (:property/integration-id property))
       "-"
       (js/encodeURIComponent (:address/full-address address))))

(defn address-list-item [{:keys [on-select-address tr lang selectable-result? municipalities municipality-names]} {:keys [property address municipality] :as result}]
  (let [on-select (fn [_]
                    (on-select-address result)
                    (>evt [::set-search-value (:address/full-address address)]))
        on-key-up (fn [event]
                    (when (= "Enter" (.-key event))
                      (on-select event)))]
    [result-list-item
     (merge
       {:id (address-result-id result)}
       (when (or (nil? selectable-result?) (selectable-result? result))
         {:on-click  on-select
          :on-key-up on-key-up}))
     (str (or (:address/full-address address) (:property/description property))
          (when-let [municipality-name (and (> (count municipalities) 1) (get municipality-names municipality))]
            (str ", " (i18n-model/get-text-from-entities-or-map municipality-name lang)))
          (if-let [property-id (:property/id property)]
            (str " (" (tr [:property/id]) ": " property-id ")")
            (str " (" (tr [:property/integration-id]) ": " (:property/integration-id property) ")")))]))

(defn location-list-item [{:keys [on-select-location selectable-result?]} {:keys [address] :as location}]
  (let [on-select (fn [_]
                    (on-select-location location)
                    (>evt [::set-search-value (:address/full-address address)]))]
    [result-list-item
     (when (or (nil? selectable-result?)
               (selectable-result? location))
       {:on-click  on-select
        :on-key-up #(case (-> % .-key)
                      "Enter" (on-select %)
                      nil)})
     (:address/full-address address)]))


(defn- address-result-key [{:keys [gis-id gis-layer property address municipality] :as result}]
  (str (or municipality "")
       "-"
       (or gis-layer :address-search)
       "-"
       (or (some-> gis-id not-empty pr-str)
           (:property/integration-id property)
           (:property/id property)
           (:address/full-address address)
           (hash result))
       ;; The fallback is skipped when an ID exists,
       ;; so append the address to distinguish rows sharing an ID.
       "-"
       (:address/full-address address)))

(defn- address-result-section-label [tr gis-layer]
  (case gis-layer
    :right-of-way-search (tr [:wizard/right-of-way])
    (tr [:wizard/parcels])))


(defn address-result-section [props gis-layer results]
  [:li
   [:div.p-2.text-xs.font-bold.uppercase.text-gray-600
    (address-result-section-label (:tr props) gis-layer)]
   [:ul {:role "group"}
    (for [address results]
      ^{:key (address-result-key address)}
      [address-list-item props address])]])


(defn search-results-list [{:keys [tr show-existing? result-list-min-width]
                            :or   {show-existing? true}
                            :as   props}
                           results
                           locations]
  [:div.absolute.z-50.shadow-lg.w-full
   {:style (if result-list-min-width
             {:width     "max-content"
              :min-width "100%"
              :max-width (str "min(" result-list-min-width ", calc(100vw - 2rem))")
              :right     0
              :left      "auto"}
             {:min-width "unset"})}
   [:div.flex.flex-row.bg-gray-200.p-2
    {:class (if result-list-min-width "overflow-y-auto" "overflow-y-scroll")
     :style {:max-height "400px"}}
    (when show-existing?
      [:div.w-half
       [:span.mb-2.flex.items-center
        (cc/icon {:icon        i/folder
                  :description "locations"
                  :fill        "currentcolor"})
        [:span.result-header.p-2 {:style {:font-weight "bolder"}} (tr [:wizard/existing-locations])]]
       [:ul {:role "listbox"}
        (map-indexed
          (fn [i location]
            ^{:key (str i "-" (get location :address))}
            [location-list-item props location])
          locations)]])
    [:div {:class (if show-existing? "w-half" "w-full")}
     [:span.mb-2.flex.items-center
      (cc/icon {:icon        i/location
                :description "addresses"
                :fill        "currentcolor"})
      [:span.result-header.p-2 {:style {:font-weight "bolder"}} (tr [:wizard/addresses])]]
     (if (:group-gis-layers? props)
       (let [result-groups (group-by :gis-layer results)]
         [:ul {:role "listbox"}
          (for [gis-layer [:right-of-way-search :address-search]
                :let      [layer-results (get result-groups gis-layer)]
                :when     (seq layer-results)]
            ^{:key (name gis-layer)}
            [address-result-section props gis-layer layer-results])])
       [:ul {:role "listbox"}
        (for [address results]
          ^{:key (address-result-key address)}
          [address-list-item props address])])]]
   (when (>= (count results) max-results)
     [:div.bg-ui-yellow.p-3.font-bold.text-center
      (tr [:wizard/too-many-results] [max-results])])])


(defn address-search
  "Expect `:tr`, `:municipalities`, `:on-select-address`, `:on-select-location`, `:domain-group-id`.
  Optional `:show-existing?` controls whether existing workspace locations are searched as well.
  Optional `:selectable-result?` predicate controls whether the caller considers an individual result as selectable.
  Optional `:group-gis-layers?` groups address search results by ROW/parcel GIS layer.
  Optional `:layer-id-fn` reads ordered GIS layer ids from app-db for multi-layer search."
  [{:keys [tr domain-group-id class disabled municipalities clear-fn on-error-evt light show-existing? placeholder
           search-input-style layer-id-fn]
    :or   {show-existing? true}                                                                                    :as props}]
  (let [search-value     (<sub [::address-search-results])
        location-matches (<sub [::location-search-results])
        pending?         (<sub [::pending?])]
    [:div.w-full.relative
     {:class     class
      :on-key-up #(case (-> % .-key)
                    "Escape" (do
                               (.focus (js/document.getElementById "address-search"))
                               (>evt [::clear-address-search]))
                    nil)}
     [ui/search {:id                      "address-search"
                 :auto-focus              true
                 :auto-complete           :off
                 :label-text              (tr [:wizard/location])
                 :placeholder             (or placeholder
                                              (tr [:wizard/search-input-placeholder]
                                                  [(property-model/format-property-id-for-sentence tr)]))
                 :close-button-label-text (tr [:wizard/empty-search])
                 :value                   (or (:search-value search-value) "")
                 :disabled                disabled
                 :search-in-progress?     (boolean pending?)
                 :on-change               #(rf/dispatch-sync [::address-search-input
                                                              domain-group-id
                                                              municipalities
                                                              (-> % futil/tval)
                                                              on-error-evt
                                                              (not show-existing?)
                                                              layer-id-fn])
                 :clear-fn                (fn [_]
                                            (>evt [::address-search-input
                                                   domain-group-id
                                                   ""])
                                            (when clear-fn
                                              (clear-fn)))
                 :loading-text            (tr [:search/loading])
                 :input-style             search-input-style
                 :light                   (boolean light)}]
     (when (not-empty (or (:results search-value)
                          (:results location-matches)))
       [:div.relative
        [search-results-list props (:results search-value) (:results location-matches)]])]))
